import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Save, RotateCcw, PlayCircle } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * WOSLASettingsPanel — 149-cw-r18.
 *
 * Panel Admin > WO SLA Reminders (BOD Lv1 only).
 * Menampilkan settings global (sph_deadline_days, po_deadline_days,
 * sph_resubmit_days, invoice_reminder_days, enabled) + tombol Dry-Run yang
 * menampilkan preview jumlah overdue + recipient tanpa dispatch.
 *
 * Endpoint:
 *   GET /api/wo-sla-settings
 *   PUT /api/wo-sla-settings
 *   POST /api/wo-sla-scan/dry-run
 */
const CATEGORY_LABEL = {
  SPH_LATE: "SPH belum dikirim",
  PO_LATE: "PO belum diterima",
  SPH_RESUBMIT_LATE: "SPH perlu revisi",
  INVOICE_LATE: "Invoice belum dibuat",
};

const DEFAULTS = {
  sph_deadline_days: 3,
  po_deadline_days: 14,
  sph_resubmit_days: 5,
  invoice_reminder_days: 7,
  enabled: true,
};

export default function WOSLASettingsPanel() {
  const { user } = useAuth();
  const isBODLv1 = user?.division === "BOD" && user?.level === 1;
  const [settings, setSettings] = useState(DEFAULTS);
  const [initial, setInitial] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [dryBusy, setDryBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wo-sla-settings");
      const s = { ...DEFAULTS, ...(data || {}) };
      setSettings(s);
      setInitial(s);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = JSON.stringify(settings) !== JSON.stringify(initial);
  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!isBODLv1) return;
    setSaving(true);
    try {
      const { data } = await api.put("/wo-sla-settings", settings);
      const next = { ...DEFAULTS, ...(data || {}) };
      setSettings(next);
      setInitial(next);
      toast.success("WO SLA settings tersimpan");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setSettings(initial);

  const runDryScan = async () => {
    setDryBusy(true);
    setDryRun(null);
    try {
      const { data } = await api.post("/wo-sla-scan/dry-run");
      setDryRun(data);
      toast.info(`${data.overdue_count} WO overdue · ${data.recipient_count} recipient`);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally {
      setDryBusy(false);
    }
  };

  if (loading) return <div className="text-xs text-slate-500 py-4 text-center">Memuat SLA settings…</div>;

  return (
    <section className="bg-white border border-slate-200" data-testid="wo-sla-settings-panel">
      <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Bell size={14} className="text-amber-600" />
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#0B1D3A]">WO SLA Reminders</div>
            <div className="text-[10px] text-slate-500 italic">
              Global settings — dijalankan setiap hari 01:00 Asia/Jakarta oleh cron.
              Notifikasi in-app + email dikirim ke user dgn permission <code>wo_reminders.active</code>.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold" data-testid="sla-dirty">
              Perubahan belum disimpan
            </span>
          )}
          <Button type="button" data-testid="sla-btn-dry-run" variant="outline"
            onClick={runDryScan} disabled={dryBusy}
            className="rounded-none h-8 text-[10px] uppercase tracking-wider flex items-center gap-1">
            <PlayCircle size={11} /> {dryBusy ? "Scan…" : "Dry-Run Scan"}
          </Button>
          {isBODLv1 && (
            <>
              <Button type="button" data-testid="sla-btn-reset" variant="outline"
                onClick={reset} disabled={!dirty || saving}
                className="rounded-none h-8 text-[10px] uppercase tracking-wider disabled:opacity-40 flex items-center gap-1">
                <RotateCcw size={11} /> Batal
              </Button>
              <Button type="button" data-testid="sla-btn-save" onClick={save}
                disabled={!dirty || saving}
                className="rounded-none h-8 text-[10px] bg-[#FF5A1F] hover:bg-[#e04a13] text-white uppercase tracking-wider disabled:opacity-40 flex items-center gap-1">
                <Save size={11} /> {saving ? "Simpan…" : "Simpan"}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <SLAField label="Batas WO → SPH (hari)" testid="sla-sph-days"
          value={settings.sph_deadline_days}
          onChange={(v) => set("sph_deadline_days", v)}
          hint="Kalau SPH belum dikirim lewat batas ini, jadi SPH_LATE."
          disabled={!isBODLv1} />
        <SLAField label="Batas SPH → PO (hari)" testid="sla-po-days"
          value={settings.po_deadline_days}
          onChange={(v) => set("po_deadline_days", v)}
          hint="Kalau PO belum diterima lewat batas ini, jadi PO_LATE."
          disabled={!isBODLv1} />
        <SLAField label="Interval Resubmit SPH (hari)" testid="sla-resubmit-days"
          value={settings.sph_resubmit_days}
          onChange={(v) => set("sph_resubmit_days", v)}
          hint="Kalau SPH terakhir sudah > interval ini tanpa PO, jadi SPH_RESUBMIT_LATE."
          disabled={!isBODLv1} />
        <SLAField label="Batas Selesai Fisik → Invoice (hari)" testid="sla-inv-days"
          value={settings.invoice_reminder_days}
          onChange={(v) => set("invoice_reminder_days", v)}
          hint="Kalau invoice belum dibuat lewat batas ini setelah Selesai Fisik, jadi INVOICE_LATE."
          disabled={!isBODLv1} />
      </div>

      <div className="px-4 pb-4 flex items-center gap-3">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="sla-enabled-label">
          <input type="checkbox"
            data-testid="sla-enabled-checkbox"
            checked={!!settings.enabled}
            onChange={(e) => set("enabled", e.target.checked)}
            disabled={!isBODLv1}
            className="h-3.5 w-3.5 accent-[#FF5A1F]" />
          <span className="text-xs text-slate-700">
            {settings.enabled ? "Aktif" : "Non-aktif"} — cron akan {settings.enabled ? "menjalankan" : "melewati"} scan harian
          </span>
        </label>
        {!isBODLv1 && (
          <span className="text-[10px] italic text-slate-500">(Hanya BOD Lv1 yang bisa mengubah settings)</span>
        )}
      </div>

      {dryRun && (
        <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="sla-dry-run-result">
          <div className="flex items-baseline gap-4 flex-wrap">
            <div>
              <div className="overline text-slate-500">Keterlambatan Ditemukan</div>
              <div className="font-display text-xl tabular-nums text-rose-700" data-testid="sla-dry-overdue-count">{dryRun.overdue_count}</div>
            </div>
            <div>
              <div className="overline text-slate-500">Recipient (in-app + email)</div>
              <div className="font-display text-xl tabular-nums text-[#0B1D3A]" data-testid="sla-dry-recipient-count">{dryRun.recipient_count}</div>
            </div>
            <span className="text-[10px] italic text-slate-500">Dry-run tidak mengirim notifikasi apapun.</span>
          </div>
          {dryRun.overdue?.length > 0 && (
            <div className="border border-slate-200 bg-white max-h-64 overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-100 sticky top-0">
                  <tr className="text-left">
                    <th className="px-2 py-1.5 overline text-slate-600">WO</th>
                    <th className="px-2 py-1.5 overline text-slate-600">Outlet</th>
                    <th className="px-2 py-1.5 overline text-slate-600">Kategori</th>
                    <th className="px-2 py-1.5 overline text-slate-600 tabular-nums text-right">Telat</th>
                    <th className="px-2 py-1.5 overline text-slate-600">Sejak</th>
                  </tr>
                </thead>
                <tbody data-testid="sla-dry-rows">
                  {dryRun.overdue.slice(0, 50).map((r) => (
                    <tr key={r.wo_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sla-dry-row-${r.wo_id}`}>
                      <td className="px-2 py-1 font-semibold text-[#0B1D3A]">{r.wo_number}</td>
                      <td className="px-2 py-1 text-slate-600">{r.outlet}</td>
                      <td className="px-2 py-1">
                        <span className="text-[10px] uppercase tracking-wider font-semibold border border-rose-300 bg-rose-50 text-rose-700 px-1.5 py-0.5">
                          {CATEGORY_LABEL[r.category] || r.category}
                        </span>
                      </td>
                      <td className="px-2 py-1 tabular-nums text-right text-rose-700 font-semibold">{r.days_overdue}h</td>
                      <td className="px-2 py-1 text-slate-600 tabular-nums">{r.since_label} · {r.since_date}</td>
                    </tr>
                  ))}
                  {dryRun.overdue.length > 50 && (
                    <tr><td colSpan={5} className="px-2 py-2 text-center text-[10px] italic text-slate-500">…dan {dryRun.overdue.length - 50} lainnya</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function SLAField({ label, value, onChange, hint, testid, disabled }) {
  return (
    <div className="space-y-1">
      <Label className="overline text-slate-600">{label}</Label>
      <Input
        type="number"
        min={1}
        max={365}
        value={value ?? ""}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (!isNaN(n)) onChange(n);
        }}
        disabled={disabled}
        data-testid={testid}
        className="rounded-none h-8 text-xs tabular-nums"
      />
      <p className="text-[10px] text-slate-500 italic">{hint}</p>
    </div>
  );
}
